import React, { useMemo } from 'react';
import { Card } from 'antd';
import {
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import { formatVnCalendarDate, formatVnChartDay } from '../../utils/vnDateTime';
import type { TrendDaily } from '../../types/dashboardFounder';
import { formatVnd, formatVndCompact } from '../../utils/formatVnd';
import { ChartSkeleton } from '../dashboard/ChartSkeleton';
import { DashboardEmptyState } from '../dashboard/DashboardEmptyState';
import { fillMissingDatesInRange } from '../../utils/dashboardFounderUtils';

interface OrdersGmvTrendChartProps {
  data: TrendDaily[];
  startDate: string;
  endDate: string;
  loading?: boolean;
}

export const OrdersGmvTrendChart: React.FC<OrdersGmvTrendChartProps> = ({
  data,
  startDate,
  endDate,
  loading = false,
}) => {
  const chartData = useMemo(() => {
    const filled = fillMissingDatesInRange(data, startDate, endDate, date => ({
      date,
      orders: 0,
      gmv: 0,
      platformFee: 0,
      voucherCost: 0,
      net: 0,
      cancelRate: 0,
      newUsers: 0,
      returningUsers: 0,
      avgCreateToConfirmMin: 0,
      avgConfirmToDeliveredMin: 0,
      avgTotalMin: 0,
    }));
    return filled.map(d => ({
      ...d,
      label: formatVnChartDay(d.date),
      aov: d.orders > 0 ? d.gmv / d.orders : 0,
    }));
  }, [data, startDate, endDate]);

  if (loading) {
    return (
      <Card className="founder-chart-card" style={{ borderRadius: 12 }}>
        <h3 className="founder-chart-title">Orders & GMV Trend</h3>
        <ChartSkeleton height={300} />
      </Card>
    );
  }

  if (!chartData.length) {
    return (
      <Card className="founder-chart-card" style={{ borderRadius: 12 }}>
        <h3 className="founder-chart-title">Orders & GMV Trend</h3>
        <DashboardEmptyState
          message="Chưa có dữ liệu"
          suggestion="Chọn khoảng ngày khác."
        />
      </Card>
    );
  }

  return (
    <Card className="founder-chart-card" style={{ borderRadius: 12 }}>
      <h3 className="founder-chart-title">Orders & GMV Trend</h3>
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart
          data={chartData}
          margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#e5e7eb"
            vertical={false}
          />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis
            yAxisId="left"
            tickFormatter={v => formatVndCompact(v)}
            width={60}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tickFormatter={v => `${v}`}
            width={36}
          />
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              const item = chartData.find(d => d.label === label);
              if (!item) return null;
              return (
                <div className="founder-tooltip">
                  <div>
                    <strong>
                      {formatVnCalendarDate(item.date, 'DD/MM/YYYY')}
                    </strong>
                  </div>
                  <div>Đơn hàng: {item.orders.toLocaleString('vi-VN')}</div>
                  <div>GMV: {formatVnd(item.gmv)}</div>
                  <div>AOV: {formatVnd(item.aov)}</div>
                </div>
              );
            }}
          />
          <Bar
            yAxisId="right"
            dataKey="orders"
            fill="#6366f1"
            radius={[4, 4, 0, 0]}
          />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="gmv"
            stroke="#2563eb"
            strokeWidth={2}
            dot={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Card>
  );
};
